import React, { useState, useEffect, useRef, useCallback } from "react";
import { message, Modal, Spin } from "antd";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import Bridge from "../../utils/Bridge";

const BACKGROUND_RECORDING_MS = 5000;
const READING_MAX_RECORDING_MS = 30000;
const PICTURE_MAX_RECORDING_MS = 60000;
// Web Audio dBFS threshold (not comparable to react-native-sound-level).
// Louder than this average = too noisy. Tune after real-device testing.
const NOISY_ENVIRONMENT_DB = -40;

const PGI_C_OPTIONS = [
  { value: 1, label: "1 = Much better" },
  { value: 2, label: "2 = A little better" },
  { value: 3, label: "3 = No change" },
  { value: 4, label: "4 = A little worse" },
  { value: 5, label: "5 = Much worse" },
];

const PGI_S_OPTIONS = [
  { value: 0, label: "0 = No speech difficulty" },
  { value: 1, label: "1 = Mild" },
  { value: 2, label: "2 = Moderate" },
  { value: 3, label: "3 = Severe" },
  { value: 4, label: "4 = Very severe" },
];

// Match LE mobile: Sound Calibration (1) + PGI (2) + Reading Text + Picture Description (1)
const PICTURE_STEP_COUNT = 1;
const CALIBRATION_AND_PGI_STEPS = 3;

const formatTimer = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const computeDbFromAnalyser = (analyser, timeData) => {
  analyser.getFloatTimeDomainData(timeData);
  let sumSquares = 0;
  for (let i = 0; i < timeData.length; i++) {
    sumSquares += timeData[i] * timeData[i];
  }
  const rms = Math.sqrt(sumSquares / timeData.length);
  if (rms < 1e-8) return -100;
  return 20 * Math.log10(rms);
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const getAudioExtension = (blob) => {
  const type = (blob?.type || "").toLowerCase();
  if (type.includes("mp4") || type.includes("m4a") || type.includes("aac")) {
    return "m4a";
  }
  if (type.includes("mpeg") || type.includes("mp3")) {
    return "mp3";
  }
  if (type.includes("ogg")) {
    return "ogg";
  }
  if (type.includes("wav")) {
    return "wav";
  }
  return "webm";
};

const getAccountId = () => localStorage.getItem("ap_participants_accounts_id");

const isApiSuccess = (response) => response?.status == 1;

const SpeechSession = () => {
  const [step, setStep] = useState("background"); // background | pgi-s | pgi-c | reading | picture | done
  const [readingIndex, setReadingIndex] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [readingBlobs, setReadingBlobs] = useState([]);
  const [pictureBlob, setPictureBlob] = useState(null);
  const [pgiCRating, setPgiCRating] = useState(null);
  const [pgiSRating, setPgiSRating] = useState(null);
  const [imageExpanded, setImageExpanded] = useState(false);
  const [environmentCheck, setEnvironmentCheck] = useState(null); // null | quiet | noisy | failed
  const [avgNoiseDb, setAvgNoiseDb] = useState(null);
  const [readingSentences, setReadingSentences] = useState([]);
  const [picturePrompt, setPicturePrompt] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const countdownIntervalRef = useRef(null);
  const stopTimeoutRef = useRef(null);
  const onRecordingCompleteRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRafRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const noiseSamplesRef = useRef([]);
  const collectNoiseSamplesRef = useRef(false);
  const sessionIdRef = useRef(null);
  const sessionDateRef = useRef(null);
  const readingSentencesRef = useRef([]);
  const picturePromptRef = useRef(null);

  const loadReadingSentences = useCallback(async () => {
    const participants_accounts_id = getAccountId();
    if (!participants_accounts_id) {
      message.warning("Unable to load reading sentences.");
      return false;
    }
    try {
      const response = await Bridge.participants.speech.session.getSentences(
        "?participants_accounts_id=" + participants_accounts_id
      );
      const sentences = Array.isArray(response?.data) ? response.data : [];
      if (isApiSuccess(response) && sentences.length > 0) {
        readingSentencesRef.current = sentences;
        setReadingSentences(sentences);
        return true;
      }
      message.warning(response?.message || "Unable to load reading sentences.");
      return false;
    } catch (err) {
      console.warn("Speech sentences fetch failed:", err);
      message.warning("Unable to load reading sentences. Please try again.");
      return false;
    }
  }, []);

  const loadPicturePrompt = useCallback(async () => {
    const participants_accounts_id = getAccountId();
    if (!participants_accounts_id) {
      message.warning("Unable to load picture.");
      return false;
    }
    try {
      const response = await Bridge.participants.speech.session.getPicture(
        "?participants_accounts_id=" + participants_accounts_id
      );
      if (isApiSuccess(response) && response?.data?.value) {
        picturePromptRef.current = response.data;
        setPicturePrompt(response.data);
        return true;
      }
      message.warning(response?.message || "Unable to load picture.");
      return false;
    } catch (err) {
      console.warn("Speech picture fetch failed:", err);
      message.warning("Unable to load picture. Please try again.");
      return false;
    }
  }, []);

  const startSpeechSession = useCallback(async () => {
    const participants_accounts_id = getAccountId();
    if (!participants_accounts_id) {
      message.warning("Unable to start speech session.");
      return;
    }
    try {
      const response = await Bridge.participants.speech.session.start({
        participants_accounts_id,
      });
      if (isApiSuccess(response) && response.session_id) {
        sessionIdRef.current = response.session_id;
        sessionDateRef.current = response.date;
        setSessionId(response.session_id);
        return;
      }
      message.warning(response?.message || "Unable to start speech session.");
    } catch (err) {
      console.warn("Speech session start failed:", err);
      message.warning("Unable to start speech session. Please try again.");
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("ap_id")) {
      window.location.assign("/");
      return;
    }
    startSpeechSession();
    loadReadingSentences();
    loadPicturePrompt();
  }, [startSpeechSession, loadReadingSentences, loadPicturePrompt]);

  const stopLevelMeter = useCallback(() => {
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    collectNoiseSamplesRef.current = false;
    setAudioLevel(0);
  }, []);

  const cleanupRecording = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    stopLevelMeter();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    recordingStartedAtRef.current = null;
  }, [stopLevelMeter]);

  useEffect(() => {
    return () => cleanupRecording();
  }, [cleanupRecording]);

  const saveCalibration = async (blob, result, avgDb) => {
    const currentSessionId = sessionIdRef.current;
    const participants_accounts_id = getAccountId();
    if (!currentSessionId || !participants_accounts_id) {
      message.warning("Unable to save recording. Please refresh and try again.");
      return false;
    }
    setIsSaving(true);
    try {
      const audio_base64 = await blobToBase64(blob);
      const passed = result === "quiet";
      const saveResponse = await Bridge.participants.speech.session.save({
        participants_accounts_id,
        session_id: currentSessionId,
        step_type: "sound_calibration",
        audio_base64,
        audio_name: `sound_calibration.${getAudioExtension(blob)}`,
        date: sessionDateRef.current,
        calibrationStatus: passed ? "pass" : "fail",
        session_status: passed ? "ongoing" : "calibration_failed",
      });
      if (!isApiSuccess(saveResponse)) {
        message.warning(saveResponse?.message || "Unable to save recording.");
        return false;
      }
      if (avgDb != null) {
        await Bridge.participants.speech.session.updateAudiodb({
          participants_accounts_id,
          session_id: currentSessionId,
          audioDB: avgDb,
          session_status: passed ? "ongoing" : "calibration_failed",
        });
      }
      return true;
    } catch (err) {
      console.warn("Speech calibration save failed:", err);
      message.warning("Unable to save recording. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveStepRecording = async ({
    blob,
    step_number,
    step_type,
    step_sentence,
    step_image,
    pool,
    startedAt,
    endedAt,
  }) => {
    const currentSessionId = sessionIdRef.current;
    const participants_accounts_id = getAccountId();
    if (!currentSessionId || !participants_accounts_id) {
      message.warning("Unable to save recording. Please refresh and try again.");
      return false;
    }
    setIsSaving(true);
    try {
      const audio_base64 = await blobToBase64(blob);
      const duration =
        startedAt && endedAt
          ? ((endedAt - startedAt) / 1000).toFixed(2)
          : "";
      const saveResponse = await Bridge.participants.speech.session.save({
        participants_accounts_id,
        session_id: currentSessionId,
        step_number,
        language: "en",
        step_type,
        step_sentence: step_sentence || null,
        step_image: step_image || null,
        pool: pool || null,
        step_audio_duration: duration,
        recording_start_time: startedAt
          ? new Date(startedAt).toISOString()
          : null,
        recording_end_time: endedAt ? new Date(endedAt).toISOString() : null,
        date: sessionDateRef.current,
        session_status: "ongoing",
        audio_base64,
        audio_name: `${step_number}.${getAudioExtension(blob)}`,
      });
      if (!isApiSuccess(saveResponse)) {
        message.warning(saveResponse?.message || "Unable to save recording.");
        return false;
      }
      return true;
    } catch (err) {
      console.warn("Speech step save failed:", err);
      message.warning("Unable to save recording. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const submitPgi = async (field, value, skipped) => {
    const currentSessionId = sessionIdRef.current;
    const participants_accounts_id = getAccountId();
    if (!currentSessionId || !participants_accounts_id) {
      message.warning("Unable to save rating. Please refresh and try again.");
      return false;
    }
    setIsSaving(true);
    try {
      const payload = {
        participants_accounts_id,
        session_id: currentSessionId,
      };
      payload[field] = skipped ? "skip" : value;
      const response = await Bridge.participants.speech.session.submitPgiScore(
        payload
      );
      if (!isApiSuccess(response)) {
        message.warning(response?.message || "Unable to save rating.");
        return false;
      }
      return true;
    } catch (err) {
      console.warn("Speech PGI save failed:", err);
      message.warning("Unable to save rating. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const startLevelMeter = (stream, { collectNoiseSamples = false } = {}) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const audioContext = new AudioCtx();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      collectNoiseSamplesRef.current = collectNoiseSamples;
      if (collectNoiseSamples) {
        noiseSamplesRef.current = [];
      }

      const freqData = new Uint8Array(analyser.frequencyBinCount);
      const timeData = new Float32Array(analyser.fftSize);

      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(freqData);
        let sum = 0;
        for (let i = 0; i < freqData.length; i++) sum += freqData[i];
        const avg = sum / freqData.length;
        const level = Math.min(5, Math.max(0, Math.round(avg / 40)));
        setAudioLevel(level);

        if (collectNoiseSamplesRef.current) {
          const db = computeDbFromAnalyser(analyserRef.current, timeData);
          if (Number.isFinite(db)) {
            noiseSamplesRef.current.push(db);
          }
        }

        levelRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn("Speech session level meter failed:", err);
    }
  };

  const evaluateEnvironmentNoise = () => {
    const readings = noiseSamplesRef.current || [];
    if (readings.length === 0) {
      setAvgNoiseDb(null);
      return { result: "failed", avgDb: null };
    }
    const avgDb =
      readings.reduce((sum, value) => sum + value, 0) / readings.length;
    setAvgNoiseDb(avgDb);
    // Higher (less negative) dB = louder. Same comparison shape as mobile LE app.
    const result = avgDb > NOISY_ENVIRONMENT_DB ? "noisy" : "quiet";
    return { result, avgDb };
  };

  const stopRecording = useCallback(() => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const startBackgroundRecording = async () => {
    if (isRecording || isSaving || step !== "background" || !sessionIdRef.current) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      message.warning("Audio recording is not supported in this browser.");
      return;
    }

    setEnvironmentCheck(null);
    setAvgNoiseDb(null);
    noiseSamplesRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType || "audio/webm",
        });
        setIsRecording(false);
        setCountdown(null);
        const { result, avgDb } = evaluateEnvironmentNoise();
        cleanupRecording();

        const saved = await saveCalibration(blob, result, avgDb);
        if (!saved) {
          setAudioBlob(null);
          return;
        }

        if (result === "quiet") {
          setAudioBlob(blob);
        } else {
          setAudioBlob(null);
        }
        setEnvironmentCheck(result);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setCountdown(5);
      startLevelMeter(stream, { collectNoiseSamples: true });

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null || prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);

      stopTimeoutRef.current = setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
        }
      }, BACKGROUND_RECORDING_MS);
    } catch (err) {
      console.warn("Speech session mic access failed:", err);
      cleanupRecording();
      setIsRecording(false);
      setCountdown(null);
      setEnvironmentCheck("failed");
      message.warning(
        "Microphone access was denied or is unavailable. Please allow microphone permission and try again."
      );
    }
  };

  const handleBackgroundMicClick = () => {
    if (isRecording || isSaving || !sessionId) return;
    startBackgroundRecording();
  };

  const handleEnvironmentContinue = () => {
    setEnvironmentCheck(null);
    setStep("pgi-s");
  };

  const handleEnvironmentRetry = () => {
    setEnvironmentCheck(null);
    setAvgNoiseDb(null);
    setAudioBlob(null);
    noiseSamplesRef.current = [];
  };

  const startLiveRecording = async ({ maxDurationMs, onComplete }) => {
    if (isRecording || isSaving || !sessionIdRef.current) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      message.warning("Audio recording is not supported in this browser.");
      return;
    }

    onRecordingCompleteRef.current = onComplete;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      recordingStartedAtRef.current = Date.now();
      setElapsedSeconds(0);
      setCountdown(null);

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType || "audio/webm",
        });
        const startedAt = recordingStartedAtRef.current;
        const endedAt = Date.now();
        setIsRecording(false);
        setCountdown(null);
        setElapsedSeconds(0);
        cleanupRecording();
        const complete = onRecordingCompleteRef.current;
        onRecordingCompleteRef.current = null;
        if (complete) complete(blob, { startedAt, endedAt });
      };

      mediaRecorder.start();
      setIsRecording(true);
      startLevelMeter(stream);

      countdownIntervalRef.current = setInterval(() => {
        if (!recordingStartedAtRef.current) return;
        const seconds = Math.floor(
          (Date.now() - recordingStartedAtRef.current) / 1000
        );
        setElapsedSeconds(seconds);
      }, 250);

      stopTimeoutRef.current = setTimeout(() => {
        stopRecording();
      }, maxDurationMs);
    } catch (err) {
      console.warn("Speech session mic access failed:", err);
      cleanupRecording();
      setIsRecording(false);
      setElapsedSeconds(0);
      onRecordingCompleteRef.current = null;
      message.warning(
        "Microphone access was denied or is unavailable. Please allow microphone permission and try again."
      );
    }
  };

  const startReadingRecording = () => {
    if (step !== "reading") return;
    const sentences = readingSentencesRef.current;
    const currentIndex = readingIndex;
    const currentSentence = sentences[currentIndex];
    if (!currentSentence) {
      message.warning("Unable to load reading sentences. Please try again.");
      return;
    }
    startLiveRecording({
      maxDurationMs: READING_MAX_RECORDING_MS,
      onComplete: async (blob, times) => {
        const saved = await saveStepRecording({
          blob,
          step_number: currentIndex + 1,
          step_type: "sentence",
          step_sentence: currentSentence.value,
          pool: currentSentence.pool,
          startedAt: times?.startedAt,
          endedAt: times?.endedAt,
        });
        if (!saved) return;
        setReadingBlobs((prev) => {
          const next = [...prev];
          next[currentIndex] = blob;
          return next;
        });
        if (currentIndex >= sentences.length - 1) {
          await goToPicture();
        } else {
          setReadingIndex(currentIndex + 1);
        }
      },
    });
  };

  const handleReadingMicClick = () => {
    if (isSaving) return;
    if (isRecording) {
      stopRecording();
    } else {
      startReadingRecording();
    }
  };

  const startPictureRecording = () => {
    if (step !== "picture") return;
    const currentPicture = picturePromptRef.current;
    if (!currentPicture?.value) {
      message.warning("Unable to load picture. Please try again.");
      return;
    }
    startLiveRecording({
      maxDurationMs: PICTURE_MAX_RECORDING_MS,
      onComplete: async (blob, times) => {
        const saved = await saveStepRecording({
          blob,
          step_number: readingSentencesRef.current.length + 1,
          step_type: "picture_description",
          step_sentence: currentPicture.picture_name || null,
          step_image: currentPicture.value,
          startedAt: times?.startedAt,
          endedAt: times?.endedAt,
        });
        if (!saved) return;
        setPictureBlob(blob);
        setStep("done");
      },
    });
  };

  const handlePictureMicClick = () => {
    if (isSaving) return;
    if (isRecording) {
      stopRecording();
    } else {
      startPictureRecording();
    }
  };

  const goToReading = async () => {
    if (readingSentencesRef.current.length === 0) {
      const loaded = await loadReadingSentences();
      if (!loaded) return;
    }
    setReadingIndex(0);
    setStep("reading");
  };

  const goToPicture = async () => {
    if (!picturePromptRef.current?.value) {
      const loaded = await loadPicturePrompt();
      if (!loaded) return;
    }
    setStep("picture");
  };

  const handlePgiCSkip = async () => {
    if (isSaving) return;
    const saved = await submitPgi("pgi_c", null, true);
    if (!saved) return;
    setPgiCRating(null);
    await goToReading();
  };

  const handlePgiCContinue = async () => {
    if (isSaving) return;
    if (pgiCRating == null) {
      message.warning("Please select a rating to continue.");
      return;
    }
    const saved = await submitPgi("pgi_c", pgiCRating, false);
    if (!saved) return;
    await goToReading();
  };

  const handlePgiSSkip = async () => {
    if (isSaving) return;
    const saved = await submitPgi("pgi_s", null, true);
    if (!saved) return;
    setPgiSRating(null);
    setStep("pgi-c");
  };

  const handlePgiSContinue = async () => {
    if (isSaving) return;
    if (pgiSRating == null) {
      message.warning("Please select a rating to continue.");
      return;
    }
    const saved = await submitPgi("pgi_s", pgiSRating, false);
    if (!saved) return;
    setStep("pgi-c");
  };

  const handleGoToAlsfrs = async () => {
    if (isEnding) return;
    const currentSessionId = sessionIdRef.current;
    const participants_accounts_id = getAccountId();
    if (!currentSessionId || !participants_accounts_id) {
      message.warning(
        "Unable to complete the speech session. Please try again."
      );
      return;
    }
    setIsEnding(true);
    try {
      const response = await Bridge.participants.speech.session.end({
        participants_accounts_id,
        session_id: currentSessionId,
      });
      if (!isApiSuccess(response)) {
        message.warning(
          response?.message || "Unable to complete the speech session."
        );
        return;
      }
      window.location.assign("/update-alsfrs?from=speech-session");
    } catch (err) {
      console.warn("Speech session end failed:", err);
      message.warning(
        "Unable to complete the speech session. Please try again."
      );
    } finally {
      setIsEnding(false);
    }
  };

  // Completed steps so far (same shape as LE mobile totalRecorded).
  const getCompletedStepCount = () => {
    switch (step) {
      case "background":
        return 0;
      case "pgi-s":
        return 1; // background done
      case "pgi-c":
        return 2; // background + pgi-s
      case "reading":
        return 3 + readingIndex; // background + both PGI + finished reading sentences
      case "picture":
        return 3 + readingSentences.length; // all reading done
      case "done":
        return totalSteps;
      default:
        return 0;
    }
  };
  const totalSteps =
    CALIBRATION_AND_PGI_STEPS + readingSentences.length + PICTURE_STEP_COUNT;
  const progressCount = getCompletedStepCount();
  const progressPercent =
    totalSteps > 0 ? Math.round((progressCount / totalSteps) * 100) : 0;

  const renderRatingOptions = (options, selectedValue, onSelect, ariaLabel) => (
    <div
      className="speech-session-pgi-options"
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const selected = selectedValue === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`speech-session-pgi-option ${
              selected ? "is-selected" : ""
            }`}
            onClick={() => onSelect(option.value)}
          >
            <span
              className={`speech-session-pgi-radio ${
                selected ? "is-selected" : ""
              }`}
              aria-hidden="true"
            />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );

  const renderMicButton = (onClick, startLabel, { allowStop = true } = {}) => (
    <button
      type="button"
      className={`speech-session-mic ${isRecording ? "is-recording" : ""}`}
      onClick={onClick}
      disabled={(isRecording && !allowStop) || isSaving || !sessionId}
      aria-label={
        isRecording
          ? allowStop
            ? `Stop recording, ${countdown ?? 0} seconds remaining`
            : `Recording, ${countdown ?? 0} seconds remaining`
          : startLabel
      }
    >
      {isRecording && countdown !== null ? (
        <span className="speech-session-countdown">{countdown}s</span>
      ) : (
        <box-icon name="microphone" type="solid" color="#fff" size="40px" />
      )}
    </button>
  );

  const renderLiveRecordingControls = (onClick, startLabel) => (
    <div className="speech-session-live-recording">
      <button
        type="button"
        className={`speech-session-mic ${isRecording ? "is-recording" : ""}`}
        onClick={onClick}
        disabled={isSaving || !sessionId}
        aria-label={isRecording ? "Stop recording" : startLabel}
      >
        {isRecording ? (
          <span className="speech-session-stop-icon" aria-hidden="true" />
        ) : (
          <box-icon name="microphone" type="solid" color="#fff" size="40px" />
        )}
      </button>

      {isRecording && (
        <>
          <p className="speech-session-recording-label">Recording...</p>
          <p className="speech-session-recording-timer">
            {formatTimer(elapsedSeconds)}
          </p>
          <div className="speech-session-level-meter" aria-hidden="true">
            {[1, 2, 3, 4, 5].map((n) => (
              <span
                key={n}
                className={`speech-session-level-bar ${
                  audioLevel >= n ? "is-active" : ""
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div>
      <Header />
      <div className="main-content speech-session-page">
        <div className="container">
          <div className="speech-session-inner">
            <a
              href="/my-personal-data-viewer"
              className="dashboard-back-arrow mt-0"
            >
              <box-icon
                name="chevron-left"
                color="#fff"
                style={{ position: "relative", top: 1 }}
              ></box-icon>
              Back
            </a>

            <Spin spinning={isSaving || isEnding}>
            <div className="speech-session-spin">
            <div className="speech-session-progress">
              <span className="speech-session-progress-count">
                {progressCount} / {totalSteps}
              </span>
              <div className="speech-session-progress-bar">
                <div
                  className="speech-session-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="speech-session-progress-percent">
                {progressPercent}%
              </span>
            </div>

            {step === "background" && (
              <div
                className="speech-session-card"
                data-has-recording={audioBlob ? "true" : "false"}
              >
                <p className="speech-session-alert">
                  Find a quiet place before beginning.
                </p>
                <h2 className="speech-session-card-title">Record Background</h2>
                <p className="speech-session-body">
                  Background noise may affect your recording. Please avoid
                  sounds from TV shows, music, or conversations.
                </p>
                <p className="speech-session-body">
                  When you are ready, we will record 5 seconds of silence.
                </p>
                <p className="speech-session-body">
                  Please stay quiet during the recording.
                </p>
                {renderMicButton(
                  handleBackgroundMicClick,
                  "Start background recording",
                  { allowStop: false }
                )}
                {isRecording && (
                  <div className="speech-session-calibration-status">
                    <p className="speech-session-recording-label">
                      Recording Background...
                    </p>
                    <p className="speech-session-body">Please stay quiet</p>
                    <div
                      className="speech-session-level-meter"
                      aria-hidden="true"
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span
                          key={n}
                          className={`speech-session-level-bar ${
                            audioLevel >= n ? "is-active" : ""
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === "pgi-c" && (
              <div className="speech-session-card speech-session-pgi-card">
                <h2 className="speech-session-card-title">
                  PGI-C – Speech Change Rating
                </h2>
                <p className="speech-session-body speech-session-pgi-question">
                  Compared with your last in-app speech session, how has your
                  speech changed?
                </p>

                {renderRatingOptions(
                  PGI_C_OPTIONS,
                  pgiCRating,
                  setPgiCRating,
                  "Speech change rating"
                )}

                <div className="speech-session-pgi-actions">
                  <button
                    type="button"
                    className="speech-session-pgi-skip"
                    onClick={handlePgiCSkip}
                    disabled={isSaving}
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    className={`speech-session-pgi-continue ${
                      pgiCRating != null ? "is-ready" : ""
                    }`}
                    onClick={handlePgiCContinue}
                    disabled={pgiCRating == null || isSaving}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {step === "pgi-s" && (
              <div className="speech-session-card speech-session-pgi-card">
                <h2 className="speech-session-card-title">
                  PGI-S - Speech Severity Rating
                </h2>
                <p className="speech-session-body speech-session-pgi-question">
                  How would you describe the severity of your speech
                  difficulties right now (for example, difficulties in speech
                  clarity, slurring, hesitations, or difficulty being
                  understood):
                </p>

                {renderRatingOptions(
                  PGI_S_OPTIONS,
                  pgiSRating,
                  setPgiSRating,
                  "Speech severity rating"
                )}

                <div className="speech-session-pgi-actions">
                  <button
                    type="button"
                    className="speech-session-pgi-skip"
                    onClick={handlePgiSSkip}
                    disabled={isSaving}
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    className={`speech-session-pgi-continue ${
                      pgiSRating != null ? "is-ready" : ""
                    }`}
                    onClick={handlePgiSContinue}
                    disabled={pgiSRating == null || isSaving}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {step === "reading" && (
              <div className="speech-session-card">
                <h2 className="speech-session-card-title">
                  Reading Text ({readingIndex + 1}/{readingSentences.length})
                </h2>
                <p className="speech-session-alert">
                  Read the sentence aloud in your normal speaking rate and
                  loudness.
                </p>
                <p className="speech-session-reading-sentence">
                  {readingSentences[readingIndex]?.value}
                </p>
                {renderLiveRecordingControls(
                  handleReadingMicClick,
                  "Start reading text recording"
                )}
              </div>
            )}

            {step === "picture" && (
              <div
                className="speech-session-card"
                data-has-picture-recording={pictureBlob ? "true" : "false"}
              >
                <h2 className="speech-session-card-title">
                  Picture Description
                </h2>
                <p className="speech-session-alert">
                  Describe what you see in the picture for about one minute in
                  English.
                </p>

                <div className="speech-session-picture-frame">
                  {picturePrompt?.value ? (
                    <img
                      src={picturePrompt.value}
                      alt={picturePrompt.picture_name || "Picture description"}
                      className="speech-session-picture-img"
                    />
                  ) : (
                    <p className="speech-session-body">Loading picture...</p>
                  )}
                  {picturePrompt?.value && (
                    <button
                      type="button"
                      className="speech-session-picture-expand"
                      onClick={() => setImageExpanded(true)}
                      aria-label="Expand picture"
                    >
                      <box-icon name="expand" color="#555" size="20px" />
                    </button>
                  )}
                </div>

                {renderLiveRecordingControls(
                  handlePictureMicClick,
                  "Start picture description recording"
                )}
              </div>
            )}

            {step === "done" && (
              <div
                className="speech-session-complete-screen"
                data-reading-count={readingBlobs.length}
              >
                <p className="speech-session-complete-message">
                  Speech portion complete. Next, tap &lsquo;Go to ALSFRS-R&rsquo;
                  to complete the ALSFRS-R questionnaire for this session.
                </p>
                <button
                  type="button"
                  className="speech-session-alsfrs-button"
                  onClick={handleGoToAlsfrs}
                  disabled={isEnding}
                >
                  Go to ALSFRS-R
                </button>
              </div>
            )}
            </div>
            </Spin>
          </div>
        </div>
      </div>
      <Footer />

      <Modal
        open={environmentCheck === "quiet"}
        title="Environment Check Complete"
        okText="Continue"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={handleEnvironmentContinue}
        onCancel={handleEnvironmentContinue}
        closable={false}
        maskClosable={false}
      >
        <p>
          The environment is suitable for recording. Proceeding to the next
          section.
          {avgNoiseDb != null ? ` (${avgNoiseDb.toFixed(1)} dB)` : ""}
        </p>
      </Modal>

      <Modal
        open={environmentCheck === "noisy"}
        title="The environment is too noisy."
        okText="Try Again"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={handleEnvironmentRetry}
        onCancel={handleEnvironmentRetry}
        closable={false}
        maskClosable={false}
      >
        <p>
          Please move to a quieter location and try again.
          {avgNoiseDb != null ? ` (${avgNoiseDb.toFixed(1)} dB)` : ""}
        </p>
      </Modal>

      <Modal
        open={environmentCheck === "failed"}
        title="Environment Check Failed"
        okText="Try Again"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={handleEnvironmentRetry}
        onCancel={handleEnvironmentRetry}
        closable={false}
        maskClosable={false}
      >
        <p>
          Background noise could not be measured. Please grant microphone
          permissions and try again.
        </p>
      </Modal>

      {imageExpanded && picturePrompt?.value && (
        <div
          className="speech-session-picture-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Expanded picture"
          onClick={() => setImageExpanded(false)}
        >
          <button
            type="button"
            className="speech-session-picture-modal-close"
            onClick={() => setImageExpanded(false)}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={picturePrompt.value}
            alt={picturePrompt.picture_name || "Picture description"}
            className="speech-session-picture-modal-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default SpeechSession;
